import React from 'react';
import { Card } from './ui/card';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';

const Rehabilitation = () => {
  const navigate = useNavigate();
  const handleBookAppointment = () => navigate('/book-appointment');

  return (
    <>
     <Header hideBookButton={true} />
    <section className="py-20 bg-gradient-to-br from-white via-slate-50 to-blue-50 relative overflow-hidden min-h-screen">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-[#204080]/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#2E5AAC]/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#204080]/10 text-[#204080] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <span>Rehabilitation</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-[#204080] mb-6 font-serif">
            Regain Strength & Restore Function
            <span className="block bg-clip-text bg-gradient-to-r from-[#204080] to-[#2E5AAC]">
              Your Journey to Full Recovery
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Tailored rehabilitation programs designed to help you heal safely and return stronger than ever.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {[
            {
              title: "Personalized Roadmaps",
              description: "Custom rehab plans aligned to your unique needs.",
            },
            {
              title: "Expert Guidance",
              description: "Work with certified therapists every step of the way.",
            },
            {
              title: "Holistic Recovery",
              description: "Focus on strength, mobility, and injury prevention.",
            },
            {
              title: "Flexible Scheduling",
              description: "Convenient sessions tailored around your life.",
            },
          ].map((benefit, idx) => (
            <Card key={idx} className="group p-6 text-center bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="space-y-4">
                <div className="inline-flex p-3 bg-gradient-to-br from-[#204080] to-[#2E5AAC] rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <span className="w-6 h-6 text-white font-bold flex items-center justify-center">✓</span>
                </div>
                <h3 className="text-lg font-bold text-[#204080] group-hover:text-[#2E5AAC] transition-colors duration-300">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* Success Metrics */}
        <Card className="p-8 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white border-0 shadow-2xl mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">Proven Recovery Results</h3>
            <p className="text-blue-100 text-lg">Helping patients regain strength and function every day</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">95%</div>
              <div className="text-blue-100 text-sm">Successful Rehab Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">80%</div>
              <div className="text-blue-100 text-sm">Faster Return to Activities</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">70%</div>
              <div className="text-blue-100 text-sm">Lower Risk of Recurrence</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">1000+</div>
              <div className="text-blue-100 text-sm">Patients Treated</div>
            </div>
          </div>
        </Card>

        {/* CTA */}
        <div className="text-center">
          <Card className="max-w-4xl mx-auto p-8 bg-white/90 backdrop-blur-sm border border-gray-200 shadow-2xl">
            <h3 className="text-2xl font-bold text-[#204080] mb-4">
              Ready to Start Your Recovery Journey?
            </h3>
            <p className="text-gray-600 mb-8 text-lg">
              Book your consultation today and let our expert team help you get back to doing what you love.
            </p>
            <div className="flex justify-center">
              <button
                className="flex items-center justify-center space-x-2 px-8 py-3 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                onClick={() => navigate('/book-appointment', { state: { selectedService: 'Rehabilitation Session' } })}
              >
                <span>Book Rehabilitation Session</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </Card>
        </div>
      </div>
    </section>
    </>
  );
};

export default Rehabilitation;
